// Frontend/src/api/notifications.ts
//
// The bell's data layer. Follows announcements.ts: plain functions, then
// hooks, one exported query key, trailing-slash URLs.
//
// The backend shipped in M1 and has been complete since — list, unread count,
// mark read, mark all read. Nothing consumed it until now.
//
// REST here is history, mutation, and fallback — never the live feed. A
// fresh unread count and list are fetched once on mount (React Query's
// normal behaviour), and from then on the cache is kept warm by
// notificationStreamBridge.ts reacting to SSE events, not by these hooks
// re-polling on a timer. `refetchInterval` only re-arms when
// useSsePollingFallbackActive() reports SSE itself is not working right now
// — see lib/notificationStream.ts and lib/sseReconnectPolicy.ts for why and
// when that happens.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "./client";
import { useSsePollingFallbackActive } from "@/lib/useSseStatus";
import type { NotificationItem, UnreadCount } from "../types/notification";

/**
 * The newest 50, or the newest 50 unread.
 *
 * The backend caps this deliberately — it is polled every 30 seconds and an
 * uncapped list means re-downloading a user's entire history twice a minute.
 * There is no page 2; the full history of anything lives on its feature page.
 */
export async function listNotifications(unreadOnly = false): Promise<NotificationItem[]> {
  const res = await api.get<NotificationItem[]>("/notifications/inbox/", {
    params: unreadOnly ? { unread: "true" } : undefined,
  });
  return res.data;
}

/** Exact and unbounded, unlike the list. It is one integer and it drives the badge. */
export async function getUnreadCount(): Promise<UnreadCount> {
  const res = await api.get<UnreadCount>("/notifications/inbox/unread_count/");
  return res.data;
}

export async function markNotificationRead(id: string): Promise<NotificationItem> {
  const res = await api.post<NotificationItem>(`/notifications/inbox/${id}/read/`);
  return res.data;
}

/**
 * "Clear all" — which marks read, and never deletes. The row is the delivery
 * record; a resident disputing that they were told about a fee increase is
 * exactly when it matters.
 */
export async function markAllNotificationsRead(): Promise<{ marked_read: number }> {
  const res = await api.post<{ marked_read: number }>("/notifications/inbox/read_all/");
  return res.data;
}

// ==================== React Query Hooks ====================

export const NOTIFICATIONS_QUERY_KEY = "notifications";

/**
 * Fallback-only cadence. This used to be the primary mechanism (a fixed
 * 30s poll, always on); it is now the safety net for when SSE itself has
 * failed repeatedly -- see useSsePollingFallbackActive().
 *
 * It deliberately does not use `usePolling` — that hook throws outside
 * <HoaAdminLayout />, and the bell goes in all three layouts.
 *
 * `refetchIntervalInBackground` is left at its default of false, so React
 * Query stops polling when the tab is hidden. Without that, every abandoned
 * tab keeps hitting the API forever.
 */
const FALLBACK_POLL_MS = 30_000;

export function useUnreadCount(enabled = true) {
  const pollingFallback = useSsePollingFallbackActive();
  return useQuery({
    queryKey: [NOTIFICATIONS_QUERY_KEY, "unread-count"],
    queryFn: getUnreadCount,
    enabled,
    refetchInterval: pollingFallback ? FALLBACK_POLL_MS : false,
    refetchOnWindowFocus: true,
    staleTime: 10_000,
  });
}

/**
 * The popover's list, and the dedicated /resident/notifications page's.
 * Only fetched while enabled — the badge comes from the count, and polling a
 * 50-row payload behind a closed popover is the exact cost this design
 * avoids. `refetchInterval` only runs while `enabled` is true, so it costs
 * nothing while the popover is shut and the page unmounted.
 */
export function useNotifications(enabled: boolean) {
  const pollingFallback = useSsePollingFallbackActive();
  return useQuery({
    queryKey: [NOTIFICATIONS_QUERY_KEY, "list"],
    queryFn: () => listNotifications(false),
    enabled,
    refetchInterval: enabled && pollingFallback ? FALLBACK_POLL_MS : false,
    refetchOnWindowFocus: true,
    staleTime: 10_000,
  });
}

/**
 * Optimistic, and rolled back on failure.
 *
 * Marking read is a side effect of navigating: the user clicks a row, the
 * popover closes and the route changes. Waiting for a round trip to update
 * the badge would show a stale count on a page the notification was about.
 */
export function useMarkRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: markNotificationRead,
    onMutate: async (id: string) => {
      await queryClient.cancelQueries({ queryKey: [NOTIFICATIONS_QUERY_KEY] });

      const previousList = queryClient.getQueryData<NotificationItem[]>([
        NOTIFICATIONS_QUERY_KEY,
        "list",
      ]);
      const previousCount = queryClient.getQueryData<UnreadCount>([
        NOTIFICATIONS_QUERY_KEY,
        "unread-count",
      ]);

      // Only move the badge if this row was actually unread — clicking an
      // already-read notification must not decrement anything.
      const wasUnread = previousList?.find((n) => n.id === id)?.is_read === false;

      if (previousList) {
        queryClient.setQueryData<NotificationItem[]>(
          [NOTIFICATIONS_QUERY_KEY, "list"],
          previousList.map((n) =>
            n.id === id ? { ...n, is_read: true, read_at: new Date().toISOString() } : n
          )
        );
      }
      if (previousCount && wasUnread) {
        queryClient.setQueryData<UnreadCount>([NOTIFICATIONS_QUERY_KEY, "unread-count"], {
          unread: Math.max(0, previousCount.unread - 1),
        });
      }

      return { previousList, previousCount };
    },
    onError: (_error, _id, context) => {
      if (context?.previousList) {
        queryClient.setQueryData([NOTIFICATIONS_QUERY_KEY, "list"], context.previousList);
      }
      if (context?.previousCount) {
        queryClient.setQueryData(
          [NOTIFICATIONS_QUERY_KEY, "unread-count"],
          context.previousCount
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [NOTIFICATIONS_QUERY_KEY] });
    },
  });
}

export function useMarkAllRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: () => {
      // The badge is the point of this button, so clear it immediately
      // rather than waiting for the refetch.
      queryClient.setQueryData<UnreadCount>([NOTIFICATIONS_QUERY_KEY, "unread-count"], {
        unread: 0,
      });
      queryClient.invalidateQueries({ queryKey: [NOTIFICATIONS_QUERY_KEY] });
    },
  });
}
